import { useState } from "react";
import { Mail, X } from "lucide-react";

/**
 * Lightweight prompt modal for collecting one or more recipient emails.
 * Replaces `window.prompt()` which is silently blocked inside sandboxed
 * iframes (e.g. the Emergent preview).
 *
 * Props:
 *   - title          : modal title (e.g. "Enviar resumen pastoral")
 *   - subtitle       : optional small heading above the title
 *   - defaultValue   : initial value for the input
 *   - placeholder    : input placeholder
 *   - confirmLabel   : button label (default "Enviar")
 *   - onConfirm(val) : called with the trimmed input value when submitted
 *   - onCancel()     : called when the user dismisses the modal
 */
export default function RecipientPromptModal({
  title,
  subtitle,
  defaultValue = "",
  placeholder = "destinatario@ejemplo.com",
  confirmLabel = "Enviar",
  onConfirm,
  onCancel,
  testid,
}) {
  const [value, setValue] = useState(defaultValue);

  const submit = (e) => {
    e?.preventDefault?.();
    if (!value.trim()) return;
    onConfirm(value.trim());
  };

  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onCancel}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white border border-[#d9cfb8] rounded shadow-xl"
        style={{ borderTop: "3px solid #c8a04a" }}
      >
        <div className="flex items-start justify-between px-5 py-3 border-b border-[#e8e1cd]">
          <div>
            {subtitle && (
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#8b7e5e]">
                {subtitle}
              </div>
            )}
            <h4 className="font-serif text-base text-[#1c1a14] mt-0.5">
              {title}
            </h4>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Cerrar"
            className="text-[#8b7e5e] hover:text-[#1c1a14]"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-2">
          <label
            htmlFor="ajar-recipient-prompt-input"
            className="block text-[10px] uppercase tracking-widest text-[#8b7e5e]"
          >
            Destinatario(s) — separa varios con coma
          </label>
          <input
            id="ajar-recipient-prompt-input"
            data-testid="recipient-prompt-input"
            autoFocus
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={placeholder}
            className="w-full border border-[#d9cfb8] bg-white px-3 py-2 text-sm text-[#1c1a14] focus:outline-none focus:border-[#c8a04a]"
          />
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#e8e1cd] bg-[#fbf7ee]">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-[#d9cfb8] bg-white px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#5d5849] hover:bg-[#f5efdf]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid="recipient-prompt-confirm"
            disabled={!value.trim()}
            className="inline-flex items-center gap-1 rounded border border-[#0f7a4a] bg-[#0f7a4a] px-3 py-1.5 text-[11px] uppercase tracking-widest text-white hover:bg-[#0a5a35] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Mail size={11} /> {confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
